import { groupBy } from 'lodash-es';
import type { ConnectedTypes } from '../../../make-connected';
import { idOf } from './util';
import { getVertexId } from '../../../../core/util';
import type { Types } from '../../../types';
import { makeChainable } from '../../../../common/make-chainable';
import type { GroupWithExclude, Wrapped } from '../../../../core/interfaces/vertices/util/edges';

/**
 * Merges group references with their parent groups while ensuring that a group is present at
 * most once and that parent groups are prioritized as they carry no member exclusions.
 */
function mergeWithParentGroups (
  references: GroupWithExclude.Util.Value<ConnectedTypes.group, ConnectedTypes.person>[],
  options:    Types.parsedToOptions
): GroupWithExclude.Util.Value<ConnectedTypes.group, ConnectedTypes.person>[] {
  return makeChainable(references)
    .chain(
      x => x.flatMap((x): ((typeof x) & { isParentGroup?: boolean })[] => ([
        x, ...(x.to.parentGroups ?? []).map(y => ({ to: y, isParentGroup: true }))
      ])),
      x => groupBy(x, x => getVertexId(x.to, options)),
      x => Object.values(x)
        .map(xs => xs.find(x => x.isParentGroup) ?? xs.at(0))
        .filter(x => x != null)
    )
    .value;
}

type ItemAndType =
  | { item: ConnectedTypes.event,      type: 'event'      }
  | { item: ConnectedTypes.lockedTime, type: 'lockedTime' };

type Source = {
  teachers?:     Wrapped.Util.Value<'to', ConnectedTypes.teacher>[];
  groups?:       GroupWithExclude.Util.Value<ConnectedTypes.group, ConnectedTypes.person>[];
  participants?: Wrapped.Util.Value<'to', ConnectedTypes.person>[];
};

/**
 * Returns the source of group, teacher, and participant references for a given item while taking into account event inheritance.
 */
function getSources (itemAndType: ItemAndType): Source {
  if (itemAndType.type == 'lockedTime') return {
    teachers: itemAndType.item.coalesced?.filter(x => x.toModel == 'teachers'),
    groups:   itemAndType.item.coalesced?.filter(x => x.toModel == 'groups'  )
  };

  const item = itemAndType.item;
  const teachersSource = item.teachers
    ? item
    : ('course' in item && item.course ? item.course : item);
  const groupsAndParticipantsSource = (item.groups || item.participants)
    ? item
    : ('course' in item && item.course ? item.course : item);
  return {
    teachers:     teachersSource.teachers                  ?? undefined,
    groups:       groupsAndParticipantsSource.groups       ?? undefined,
    participants: groupsAndParticipantsSource.participants ?? undefined
  };
}

/**
 * Parses group references from a course or event while carrying out event inheritance and taking into account parent groups.
 */
export function parseGroupReferences (
  itemAndType: ItemAndType,
  options:     Types.parsedToOptions
): Types.Shared.GroupReferenceSets {
  const sources = getSources(itemAndType);

  const teacherReferences = (sources.teachers ?? [])
    .map((x): Types.Shared.GroupReference => ({ group: idOf.teacher(x.to, options) }));

  const groupReferences = mergeWithParentGroups(sources.groups ?? [], options)
    .map((x): Types.Shared.GroupReference | Types.Shared.IndividualsReference => {
      if (x.to.species != 'class' || x.exclude?.length) return { individuals: idOf.groupReference(x, options) };   // use individuals set if not a plain group reference
      return { group: idOf.group(x.to, options) };
    });

  const participantReferences = sources.participants?.length
    ? { individuals: sources.participants.map(x => idOf.person(x.to, options)) } as Types.Shared.IndividualsReference
    : undefined;

  // combine all references
  return new Array<Types.Shared.GroupReference | Types.Shared.IndividualsReference>()
    .concat(
      teacherReferences,
      groupReferences,
      participantReferences ? [participantReferences] : []
    )
    .map(x => [x]);
}